import { Badge } from '@chakra-ui/react'
import { FE_VISIT_SUPERSEDED_LABEL, FE_VISIT_SUPERSEDED_STATUS } from './taskUtils'

type StatusCatalogEntry = {
  code: string
  label?: string | null
  scope?: string | null
  is_terminal?: boolean
  color?: string | null
  sort_order?: number | null
}

type Props = {
  status?: string | null
  statusLabel?: string | null
  statusRecord?: StatusCatalogEntry | null
}

function resolveLabel(status?: string | null, statusLabel?: string | null, statusRecord?: StatusCatalogEntry | null): string {
  if ((status ?? '') === FE_VISIT_SUPERSEDED_STATUS) {
    return FE_VISIT_SUPERSEDED_LABEL
  }
  return statusRecord?.label?.trim() || statusLabel?.trim() || status?.trim() || 'Unknown'
}

function colorScheme(
  status?: string | null,
  statusRecord?: StatusCatalogEntry | null
): string {
  if (statusRecord?.color) {
    return statusRecord.color
  }

  const code = (status ?? '').toLowerCase()

  // Explicit FE visit codes — must run before is_terminal / substring fallbacks
  // (e.g. fe_visit_cancelled is terminal but should stay red, not green).
  if (code === 'fe_visit_rejected') return 'red'
  if (code === 'fe_visit_revisit') return 'orange'
  if (code === 'fe_visit_cancelled') return 'red'
  if (code === 'fe_visit_superseded') return 'purple'
  if (code === 'fe_visit_issue_with_documents') return 'orange'

  if (statusRecord?.is_terminal) return 'green'

  const label = resolveLabel(status, null, statusRecord).toLowerCase()

  if (
    code.includes('progress') ||
    code.includes('started') ||
    code.includes('active') ||
    label.includes('in progress')
  ) {
    return 'cyan'
  }
  if (
    code.includes('pending') ||
    code.includes('waiting') ||
    code.includes('hold') ||
    label.includes('pending') ||
    label.includes('waiting')
  ) {
    return 'yellow'
  }
  if (code.includes('skip') || code.includes('cancel') || label.includes('skipped')) {
    return 'gray'
  }
  if (code.includes('complete') || code.includes('done') || code.includes('resolved')) {
    return 'green'
  }
  if (code.includes('block') || code.includes('fail') || label.includes('blocked')) {
    return 'red'
  }

  return 'blue'
}

export default function TaskStatusPill({ status, statusLabel, statusRecord }: Props) {
  const label = resolveLabel(status, statusLabel, statusRecord)

  return (
    <Badge
      variant="outline"
      colorScheme={colorScheme(status, statusRecord)}
      borderRadius="md"
      px={3}
      py={1}
      fontWeight="600"
      fontSize="sm"
      textTransform="none"
      maxW="220px"
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      title={label}
    >
      {label}
    </Badge>
  )
}
